import type { PanelLayout } from '$lib/services/preferences';

export interface PanelLayoutStore {
  panelLayout(panel: string): PanelLayout | null;
  setPanelLayout(panel: string, layout: PanelLayout): void;
}

export interface PanelWidth {
  readonly min: number;
  readonly default: number;
  readonly max: number;
}

export const PANEL_WIDTH = { min: 200, default: 272, max: 480 } as const;
export const SCRIPT_WIDTH = { min: 320, default: 512, max: 800 } as const;

/** A column beside the canvas that hides and resizes, remembered per browser. */
export class SidePanel {
  collapsed = $state(false);
  width: number = $state(0);
  #others: readonly SidePanel[] = [];

  constructor(
    private readonly name: string,
    readonly limits: PanelWidth,
    private readonly store: PanelLayoutStore,
  ) {
    const layout = store.panelLayout(name);
    this.collapsed = layout?.collapsed ?? false;
    this.width = this.#clamp(layout?.width ?? limits.default);
  }

  /** Follows a resize drag; `finishResize` remembers where it ended. */
  resize(width: number): void {
    this.width = this.#clamp(width);
  }

  finishResize(): void {
    this.persist();
  }

  setCollapsed(collapsed: boolean): void {
    this.collapsed = collapsed;
    this.persist();
    if (!collapsed) this.closeOthers();
  }

  /** Keeps at most one of `panels` open; the last one open wins when several are. */
  static oneOpenAmong(...panels: SidePanel[]): void {
    for (const panel of panels) panel.#others = panels.filter((other) => other !== panel);
    const open = panels.filter((panel) => !panel.collapsed);
    open.at(-1)?.closeOthers();
  }

  protected closeOthers(): void {
    for (const other of this.#others) if (!other.collapsed) other.setCollapsed(true);
  }

  protected layout(): PanelLayout {
    return { collapsed: this.collapsed, width: this.width };
  }

  protected persist(): void {
    this.store.setPanelLayout(this.name, this.layout());
  }

  #clamp(width: number): number {
    return Math.round(Math.min(this.limits.max, Math.max(this.limits.min, width)));
  }
}

/** Which section a side panel's column shows, and whether the column is hidden. */
export class PanelSections extends SidePanel {
  shown: string = $state('');

  constructor(
    name: string,
    limits: PanelWidth,
    ids: readonly string[],
    store: PanelLayoutStore,
    initial: string = ids[0] ?? '',
  ) {
    super(name, limits, store);
    const shown = store.panelLayout(name)?.shown;
    this.shown = ids.find((id) => id === shown) ?? initial;
  }

  isShown(id: string): boolean {
    return !this.collapsed && this.shown === id;
  }

  /** Shows `id` alone, opening the column if it is hidden; never hides it. */
  open(id: string): void {
    this.collapsed = false;
    this.shown = id;
    this.persist();
    this.closeOthers();
  }

  /** Shows `id` alone; asked again while it is shown, hides the column. */
  show(id: string): void {
    this.collapsed = this.isShown(id);
    this.shown = id;
    this.persist();
    if (!this.collapsed) this.closeOthers();
  }

  protected override layout(): PanelLayout {
    return { ...super.layout(), shown: this.shown };
  }
}
